import { useEffect, useRef, useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import type { UnifiedEpisode } from '../../anime/UnifiedAnime.ts';
import type { HomeMedia } from '../../metadata/HomeRecommendations.ts';
import { createAnimeSource } from '../../anime/AnimeRepository.ts';
import { sourceHttpClient } from '../../core/SourceHttp.ts';
import { isDirectVideo, saveAnimeEpisode } from '../../anime/AnimeDownloads.ts';
import { chooseDownloadFolder } from '../../storage/DownloadFolder.ts';
import { mediaTitle } from '../../metadata/HomeRecommendations.ts';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import {
  X,
  FolderOpen,
  Download,
  CheckSquare,
  Square,
  AlertTriangle,
  CheckCircle2,
  SlidersHorizontal,
  ChevronDown,
  Layers,
  ArrowRight
} from 'lucide-react';
import './download-dialog.css';

export function UnifiedBulkDownload({
  media,
  episodes,
  open,
  onClose,
}: {
  media: HomeMedia;
  episodes: UnifiedEpisode[];
  open: boolean;
  onClose: () => void;
}) {
  const [selection, setSelection] = useState<string[]>([]);
  const [source, setSource] = useState('auto');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [messages, setMessages] = useState<string[]>([]);
  const [folder, setFolder] = useState('');
  const [from, setFrom] = useState(1);
  const [to, setTo] = useState(1);
  const [showAdvancedList, setShowAdvancedList] = useState(false);
  const [transfer, setTransfer] = useState({
    stage: '',
    episode: '',
    source: '',
    bytes: 0,
    total: 0,
    rate: 0,
    done: 0,
    count: 0,
  });
  const [elapsed, setElapsed] = useState(0);
  const begun = useRef(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!busy) return;
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - begun.current) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);

  const size = (bytes: number) =>
    bytes >= 1048576 ? (bytes / 1048576).toFixed(1) + ' MB' : (bytes / 1024).toFixed(0) + ' KB';
  const duration = (seconds: number) =>
    seconds < 60 ? Math.ceil(seconds) + ' s' : Math.ceil(seconds / 60) + ' min';

  useEffect(() => {
    if (open) {
      setFrom(1);
      const total = Math.max(1, episodes.length);
      setTo(total);
      setSelection(episodes.map((row) => row.key));
      setMessages([]);
      setProgress(0);
      setShowAdvancedList(false);

      void yomiriDatabase.getSetting('custom_directory_handle').then((handle) => {
        if (handle) {
          void yomiriDatabase
            .getSetting<{ directoryName: string }>('storage_folder_config')
            .then((config) => setFolder(config?.directoryName || 'Yomiri'));
        } else {
          setFolder('');
        }
      });
    }
  }, [open, episodes]);

  useEffect(() => () => controller.current?.abort(), []);

  const sources = useMemo(
    () => [...new Map(episodes.flatMap((row) => row.variants.map((v) => [v.sourceId, v.sourceName] as const))).entries()],
    [episodes]
  );

  const applyRange = (start: number, end: number) => {
    const s = Math.max(1, Math.min(start, episodes.length || 1));
    const e = Math.max(s, Math.min(end, episodes.length || 1));
    setFrom(s);
    setTo(e);
    setSelection(episodes.slice(s - 1, e).map((row) => row.key));
  };

  const selectQuick = (count: number) => {
    if (count === -1) {
      applyRange(1, episodes.length);
    } else {
      applyRange(1, Math.min(count, episodes.length));
    }
  };

  const toggleEpisode = (key: string) => {
    setSelection((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const run = async () => {
    const abort = new AbortController();
    controller.current = abort;
    begun.current = Date.now();
    setElapsed(0);
    setTransfer({
      stage: 'Iniciando descarga',
      episode: '',
      source: '',
      bytes: 0,
      total: 0,
      rate: 0,
      done: 0,
      count: selection.length,
    });
    setProgress(0);
    setBusy(true);
    setMessages([]);
    let completed = 0;

    try {
      const preferred = await yomiriDatabase.getSetting<string>(`anime-preferred-source:${media.id}`, '');
      const rows = episodes.filter((row) => selection.includes(row.key));

      for (const row of rows) {
        abort.signal.throwIfAborted();
        setTransfer((t) => ({
          ...t,
          stage: 'Buscando servidor disponible',
          episode: row.name,
          done: completed,
          bytes: 0,
          total: 0,
          rate: 0,
        }));

        const variant =
          source === 'auto'
            ? row.variants.find((item) => item.sourceId === preferred) || row.variants[0]
            : row.variants.find((item) => item.sourceId === source);

        if (!variant) {
          setMessages((prev) => [...prev, `${row.name}: no está disponible en la fuente elegida.`]);
          completed++;
          setProgress((completed / rows.length) * 100);
          setTransfer((t) => ({ ...t, done: completed }));
          continue;
        }

        try {
          const variants = source === 'auto' ? [variant, ...row.variants.filter((v) => v !== variant)] : [variant];
          let saved = false;
          const failures: string[] = [];

          for (const candidate of variants) {
            try {
              setTransfer((t) => ({ ...t, stage: 'Obteniendo servidores', source: candidate.sourceName }));
              const servers = await createAnimeSource(candidate.sourceId, sourceHttpClient).getServers(
                candidate.episode.id
              );
              abort.signal.throwIfAborted();
              const direct = servers.filter(isDirectVideo);
              if (!direct.length) throw new Error('Sin video directo compatible');

              for (const srv of direct) {
                try {
                  const started = performance.now();
                  setTransfer((t) => ({ ...t, stage: 'Conectando con servidor de video', bytes: 0, total: 0, rate: 0 }));
                  await saveAnimeEpisode(
                    candidate.anime,
                    candidate.episode,
                    { ...srv, url: srv.downloadUrl || srv.url },
                    abort.signal,
                    (bytes, total) => {
                      setProgress(((completed + (total ? Math.min(1, bytes / total) : 0)) / rows.length) * 100);
                      setTransfer((t) => ({
                        ...t,
                        stage: 'Descargando video',
                        bytes,
                        total,
                        rate: bytes / Math.max(0.1, (performance.now() - started) / 1000),
                      }));
                    }
                  );
                  saved = true;
                  break;
                } catch (error) {
                  if (abort.signal.aborted) throw error;
                  failures.push(error instanceof Error ? error.message : 'Error en servidor');
                }
              }
              if (saved) break;
            } catch (error) {
              if (abort.signal.aborted) throw error;
              failures.push(candidate.sourceName + ': ' + (error instanceof Error ? error.message : 'Sin respuesta'));
            }
          }

          if (!saved) throw new Error(failures.join(' · ') || 'No hay enlaces directos para descargar.');
          setMessages((prev) => [...prev, `${row.name}: completado correctamente.`]);
        } catch (cause) {
          if (abort.signal.aborted) throw cause;
          setMessages((prev) => [
            ...prev,
            `${row.name}: ${cause instanceof Error ? cause.message : 'Falló la descarga.'}`,
          ]);
        }

        completed++;
        setProgress((completed / rows.length) * 100);
        setTransfer((t) => ({ ...t, done: completed }));
      }
    } catch (cause) {
      if (!abort.signal.aborted && cause instanceof DOMException && cause.name === 'AbortError') return;
      setMessages((prev) => [
        ...prev,
        abort.signal.aborted
          ? 'Descarga pausada o cancelada. Se mantuvieron los archivos finalizados.'
          : cause instanceof Error
          ? cause.message
          : 'No se pudo descargar.',
      ]);
    } finally {
      setTransfer((t) => ({ ...t, stage: abort.signal.aborted ? 'Cancelado' : 'Finalizado' }));
      setBusy(false);
      controller.current = null;
    }
  };

  const pickFolder = () => {
    setMessages([]);
    void chooseDownloadFolder()
      .then((name) => setFolder(name))
      .catch((cause) => {
        if (cause?.name !== 'AbortError') {
          setMessages(['No se pudo abrir la carpeta. Reintentá seleccionarla.']);
        }
      });
  };

  return (
    <Dialog
      className="y-download-dialog yomiri-panel"
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle className="y-download-heading">
        <div className="y-dl-title-group">
          <Download size={18} className="y-dl-title-icon" />
          <span>Descargar episodios</span>
        </div>
        <button
          aria-label="Cerrar ventana de descarga"
          disabled={busy}
          className="y-dl-close-btn"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </DialogTitle>

      <DialogContent className="y-dl-content">
        {/* Work Brief Card */}
        <div className="y-dl-work-card">
          <img src={media.coverImage.large} alt="" className="y-dl-work-cover" />
          <div className="y-dl-work-meta">
            <h3 className="y-dl-work-title">{mediaTitle(media)}</h3>
            <span className="y-dl-work-subtitle">
              {episodes.length} {episodes.length === 1 ? 'episodio disponible' : 'episodios disponibles'}
            </span>
          </div>
        </div>

        {/* Live Transfer Status Banner */}
        {transfer.stage && (
          <section className="y-dl-progress-box" role="status" aria-live="polite">
            <div className="y-dl-progress-header">
              <div>
                <strong className="y-dl-stage-text">{transfer.stage}</strong>
                {transfer.episode && (
                  <span className="y-dl-stage-sub">
                    {transfer.episode}
                    {transfer.source ? ` · ${transfer.source}` : ''}
                  </span>
                )}
              </div>
              <span className="y-dl-counter-badge">
                {transfer.done} / {transfer.count}
              </span>
            </div>

            <div className="y-dl-meter-track">
              <div
                className={`y-dl-meter-fill ${busy && transfer.total === 0 ? 'is-pulsing' : ''}`}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>

            <div className="y-dl-stats-row">
              <span>
                {transfer.bytes
                  ? `${size(transfer.bytes)}${transfer.total ? ` / ${size(transfer.total)}` : ''}`
                  : busy
                  ? 'Buscando stream...'
                  : ''}
              </span>
              <span>{transfer.rate > 0 ? `${size(transfer.rate)}/s` : ''}</span>
            </div>

            <div className="y-dl-stats-eta">
              {busy && transfer.total > transfer.bytes && transfer.rate > 0 && (
                <span>Restante aprox: {duration((transfer.total - transfer.bytes) / transfer.rate)}</span>
              )}
              {elapsed > 0 && <span>Tiempo transcurrido: {duration(elapsed)}</span>}
            </div>
          </section>
        )}

        {/* Source Selector Control */}
        <div className="y-dl-control-group">
          <label className="y-dl-field-label" htmlFor="dl-source-select">
            Proveedor / Fuente
          </label>
          <div className="y-dl-select-wrapper">
            <select
              id="dl-source-select"
              disabled={busy}
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="y-dl-input"
            >
              <option value="auto">Automática (prioriza tu fuente favorita y prueba alternativas)</option>
              {sources.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="y-dl-select-arrow" />
          </div>
        </div>

        {/* Episode Selection Section */}
        <div className="y-dl-control-group">
          <div className="y-dl-section-bar">
            <span className="y-dl-field-label">Selección de episodios</span>
            <span className="y-dl-count-pill">{selection.length} seleccionados</span>
          </div>

          {/* Quick Preset Buttons */}
          <div className="y-dl-presets-bar">
            <button
              type="button"
              disabled={busy || !episodes.length}
              className={`y-dl-preset-chip ${selection.length === episodes.length ? 'is-active' : ''}`}
              onClick={() => selectQuick(-1)}
            >
              Todos ({episodes.length})
            </button>
            {episodes.length > 5 && (
              <button
                type="button"
                disabled={busy}
                className="y-dl-preset-chip"
                onClick={() => selectQuick(5)}
              >
                Primeros 5
              </button>
            )}
            {episodes.length > 12 && (
              <button
                type="button"
                disabled={busy}
                className="y-dl-preset-chip"
                onClick={() => selectQuick(12)}
              >
                Primeros 12
              </button>
            )}
          </div>

          {/* Numeric Range Inputs */}
          <div className="y-dl-range-card">
            <div className="y-dl-range-field">
              <label htmlFor="dl-from-ep">Desde cap.</label>
              <input
                id="dl-from-ep"
                type="number"
                min={1}
                max={episodes.length || 1}
                disabled={busy}
                value={from}
                onChange={(e) => applyRange(Number(e.target.value), to)}
                className="y-dl-input y-dl-range-num"
              />
            </div>
            <ArrowRight size={14} className="y-dl-range-sep" />
            <div className="y-dl-range-field">
              <label htmlFor="dl-to-ep">Hasta cap.</label>
              <input
                id="dl-to-ep"
                type="number"
                min={from}
                max={episodes.length || 1}
                disabled={busy}
                value={to}
                onChange={(e) => applyRange(from, Number(e.target.value))}
                className="y-dl-input y-dl-range-num"
              />
            </div>
          </div>

          {/* Granular Custom Checkboxes Toggle */}
          <div className="y-dl-accordion">
            <button
              type="button"
              className="y-dl-accordion-trigger"
              disabled={busy}
              onClick={() => setShowAdvancedList((v) => !v)}
            >
              <SlidersHorizontal size={13} />
              <span>Elegir episodios uno por uno</span>
              <ChevronDown
                size={14}
                style={{ transform: showAdvancedList ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            </button>

            {showAdvancedList && (
              <div className="y-dl-grid-selector">
                {episodes.map((row) => {
                  const isChecked = selection.includes(row.key);
                  return (
                    <button
                      type="button"
                      key={row.key}
                      disabled={busy}
                      className={`y-dl-ep-chip ${isChecked ? 'is-selected' : ''}`}
                      onClick={() => toggleEpisode(row.key)}
                    >
                      {isChecked ? <CheckSquare size={13} /> : <Square size={13} />}
                      <span>{row.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Destination Folder Selector */}
        <div className="y-dl-control-group">
          <span className="y-dl-field-label">Destino de guardado</span>
          <button
            type="button"
            className="y-dl-folder-button"
            disabled={busy}
            onClick={pickFolder}
          >
            <FolderOpen size={16} className="y-dl-folder-icon" />
            <div className="y-dl-folder-texts">
              <span className="y-dl-folder-path">
                {folder ? `${folder}/Anime` : 'Descargas del navegador (predeterminado)'}
              </span>
              <span className="y-dl-folder-hint">
                {folder ? 'Carpeta local personalizada activa' : 'Hacé clic para elegir una carpeta directa'}
              </span>
            </div>
            <span className="y-dl-folder-action">Cambiar</span>
          </button>
        </div>

        {/* Message Log */}
        {messages.length > 0 && (
          <div className="y-dl-messages-box" role="status">
            {messages.map((msg, i) => (
              <div key={i} className="y-dl-message-item">
                <CheckCircle2 size={13} className="y-dl-msg-icon" />
                <span>{msg}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>

      <DialogActions className="y-dl-actions">
        {busy ? (
          <button
            type="button"
            className="nyomiri-btn nyomiri-btn-neutral y-dl-cancel-btn"
            onClick={() => controller.current?.abort()}
          >
            Detener descarga
          </button>
        ) : (
          <>
            <button
              type="button"
              className="nyomiri-btn nyomiri-btn-neutral"
              onClick={onClose}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="nyomiri-btn nyomiri-btn-primary"
              disabled={!selection.length || from > to}
              onClick={() => void run()}
            >
              <Download size={15} />
              <span>Descargar {selection.length} {selection.length === 1 ? 'capítulo' : 'capítulos'}</span>
            </button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
